type AuthDebugFields = Record<string, unknown>;

const SENSITIVE_KEY_PATTERN =
  /password|token|secret|authorization|jwt|idtoken|accesstoken|refreshtoken|challengeresponse/i;
const EMAIL_KEY_PATTERN = /email|username|useremail/i;
const ID_KEY_PATTERN = /poolid|clientid|usersub|sub$/i;

export const isAuthDebugEnabled = (): boolean => {
  if (typeof window === 'undefined') return false;

  try {
    return new URLSearchParams(window.location.search).get('debugAuth') === '1';
  } catch (_err) {
    return false;
  }
};

export const maskEmail = (value: string): string => {
  const normalized = value.trim().toLowerCase();
  const atIndex = normalized.indexOf('@');
  if (atIndex < 0) return maskId(normalized);

  const local = normalized.slice(0, atIndex);
  const domain = normalized.slice(atIndex + 1);
  return `${local.charAt(0) || '*'}***@${domain}`;
};

export const maskId = (value: string): string => {
  const trimmed = value.trim();
  if (trimmed.length <= 8) return trimmed ? `[${trimmed.length} chars]` : '';
  return `${trimmed.slice(0, 4)}...${trimmed.slice(-4)}`;
};

export const getAuthDebugError = (error: unknown): { name?: string; message: string } => {
  if (error instanceof Error) {
    return {
      name: error.name || 'Error',
      message: error.message || 'Unknown error',
    };
  }

  if (typeof error === 'object' && error !== null) {
    const record = error as Record<string, unknown>;
    return {
      ...(typeof record.name === 'string' ? { name: record.name } : {}),
      message: typeof record.message === 'string' ? record.message : 'Unknown error',
    };
  }

  return {
    message: typeof error === 'string' ? error : String(error),
  };
};

const looksLikeEmail = (value: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const maskEmbeddedEmails = (value: string): string =>
  value.replace(/[^\s@]+@[^\s@]+\.[^\s@.,;:)]+/g, (match) => maskEmail(match));

const sanitizeValue = (key: string, value: unknown, depth = 0): unknown => {
  if (SENSITIVE_KEY_PATTERN.test(key)) return '[redacted]';

  if (value instanceof Error) return getAuthDebugError(value);

  if (typeof value === 'string') {
    if (EMAIL_KEY_PATTERN.test(key) || looksLikeEmail(value)) return maskEmail(value);
    if (ID_KEY_PATTERN.test(key)) return maskId(value);
    return maskEmbeddedEmails(value);
  }

  if (Array.isArray(value)) {
    return value.map((item) => sanitizeValue(key, item, depth + 1));
  }

  if (value && typeof value === 'object' && depth < 2) {
    return Object.fromEntries(
      Object.entries(value as AuthDebugFields).map(([entryKey, entryValue]) => [
        entryKey,
        sanitizeValue(entryKey, entryValue, depth + 1),
      ]),
    );
  }

  return value;
};

const sanitizeFields = (fields: AuthDebugFields): AuthDebugFields =>
  Object.fromEntries(
    Object.entries(fields).map(([key, value]) => [key, sanitizeValue(key, value)]),
  );

export const authDebug = (event: string, fields: AuthDebugFields = {}): void => {
  if (!isAuthDebugEnabled()) return;

  try {
    console.info('[auth-debug]', event, sanitizeFields(fields));
  } catch (_err) {
    // Debug logging must never affect auth flow.
  }
};
